import React, { useState } from 'react';
import { Modal, Carousel, Button } from 'react-bootstrap';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import OptimizedImage from './OptimizedImage';

/**
 * ImageGallery - Visualizador de galeria estilo Airbnb com Bootstrap
 *
 * @param {Array} images - Array de objetos com {image_url, caption, is_primary}
 * @param {string} alt - Texto alternativo base
 */
const ImageGallery = ({ images = [], alt = 'Imagem da propriedade' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Ordena imagens: primária primeiro, depois por ordem
  const sortedImages = [...images].sort((a, b) => {
    if (a.is_primary) return -1;
    if (b.is_primary) return 1;
    return (a.order || 0) - (b.order || 0);
  });

  const openGallery = (index) => {
    setCurrentIndex(index);
    setIsOpen(true);
  };

  const closeGallery = () => {
    setIsOpen(false);
  };

  const handleSelect = (selectedIndex) => {
    setCurrentIndex(selectedIndex);
  };

  if (!sortedImages.length) return null;

  return (
    <>
      {/* Grid de Miniaturas */}
      <div className="position-relative">
        {sortedImages.length === 1 ? (
          // Layout para uma única imagem
          <button
            onClick={() => openGallery(0)}
            className="border-0 p-0 w-100 position-relative overflow-hidden"
            style={{ borderRadius: '12px', height: '500px' }}
          >
            <OptimizedImage
              src={sortedImages[0].image_url}
              width={1200}
              height={800}
              mode="cover"
              alt={alt}
              className="w-100 h-100"
              style={{ objectFit: 'cover' }}
              lazy={false}
            />
          </button>
        ) : sortedImages.length === 2 ? (
          // Layout para duas imagens
          <div className="d-flex gap-2" style={{ height: '500px' }}>
            {sortedImages.slice(0, 2).map((image, index) => (
              <button
                key={index}
                onClick={() => openGallery(index)}
                className="border-0 p-0 flex-fill position-relative overflow-hidden"
                style={{
                  borderRadius: index === 0 ? '12px 0 0 12px' : '0 12px 12px 0'
                }}
              >
                <OptimizedImage
                  src={image.image_url}
                  width={600}
                  height={800}
                  mode="cover"
                  alt={`${alt} ${index + 1}`}
                  className="w-100 h-100"
                  style={{ objectFit: 'cover' }}
                  lazy={index > 0}
                />
              </button>
            ))}
          </div>
        ) : (
          // Layout para 3+ imagens (estilo Airbnb)
          <div className="d-grid gap-2" style={{
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: '1fr 1fr',
            height: '500px'
          }}>
            <button
              onClick={() => openGallery(0)}
              className="border-0 p-0 position-relative overflow-hidden"
              style={{
                gridRow: '1 / 3',
                borderRadius: '12px 0 0 12px'
              }}
            >
              <OptimizedImage
                src={sortedImages[0].image_url}
                width={800}
                height={800}
                mode="cover"
                alt={alt}
                className="w-100 h-100"
                style={{ objectFit: 'cover' }}
                lazy={false}
              />
            </button>
            {sortedImages.slice(1, 5).map((image, index) => (
              <button
                key={index + 1}
                onClick={() => openGallery(index + 1)}
                className="border-0 p-0 position-relative overflow-hidden"
                style={{
                  borderRadius: index === 1 ? '0 12px 0 0' : index === 3 ? '0 0 12px 0' : '0'
                }}
              >
                <OptimizedImage
                  src={image.image_url}
                  width={400}
                  height={200}
                  mode="cover"
                  alt={`${alt} ${index + 2}`}
                  className="w-100 h-100"
                  style={{ objectFit: 'cover' }}
                  lazy={true}
                />
              </button>
            ))}
          </div>
        )}

        {/* Botão "Ver todas as fotos" */}
        {sortedImages.length > 1 && (
          <Button
            variant="light"
            size="sm"
            onClick={() => openGallery(0)}
            className="position-absolute bottom-0 end-0 m-3 d-flex align-items-center gap-2 shadow-sm"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>Ver todas as {sortedImages.length} fotos</span>
          </Button>
        )}
      </div>

      {/* Modal de Visualização com Carousel */}
      <Modal
        show={isOpen}
        onHide={closeGallery}
        size="xl"
        centered
        fullscreen="lg-down"
        dialogClassName="modal-90w"
      >
        <Modal.Header className="border-0 pb-0">
          <div className="d-flex justify-content-between align-items-center w-100">
            <Button
              variant="light"
              size="sm"
              onClick={closeGallery}
              className="rounded-circle p-2"
              style={{ width: '40px', height: '40px' }}
            >
              <FiX size={20} />
            </Button>
            <span className="fw-medium">
              {currentIndex + 1} / {sortedImages.length}
            </span>
          </div>
        </Modal.Header>

        <Modal.Body className="p-0">
          <Carousel
            activeIndex={currentIndex}
            onSelect={handleSelect}
            interval={null}
            indicators={false}
            prevIcon={
              <div
                className="bg-white rounded-circle shadow d-flex align-items-center justify-content-center"
                style={{ width: '48px', height: '48px' }}
              >
                <FiChevronLeft size={24} color="#000" />
              </div>
            }
            nextIcon={
              <div
                className="bg-white rounded-circle shadow d-flex align-items-center justify-content-center"
                style={{ width: '48px', height: '48px' }}
              >
                <FiChevronRight size={24} color="#000" />
              </div>
            }
          >
            {sortedImages.map((image, index) => (
              <Carousel.Item key={index}>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                  <OptimizedImage
                    src={image.image_url}
                    width={1920}
                    height={1080}
                    mode="fit"
                    alt={`${alt} ${index + 1}`}
                    className="d-block"
                    style={{ maxHeight: '80vh', maxWidth: '100%', objectFit: 'contain' }}
                    lazy={false}
                  />
                </div>
                {image.caption && (
                  <Carousel.Caption className="position-relative bg-transparent text-dark">
                    <p className="mb-0">{image.caption}</p>
                  </Carousel.Caption>
                )}
              </Carousel.Item>
            ))}
          </Carousel>

          {/* Miniaturas */}
          {sortedImages.length > 1 && (
            <div className="p-3 bg-light border-top overflow-auto">
              <div className="d-flex gap-2">
                {sortedImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`border rounded overflow-hidden flex-shrink-0 ${
                      index === currentIndex ? 'border-primary border-3' : 'border-secondary'
                    }`}
                    style={{ width: '80px', height: '80px', padding: '0' }}
                  >
                    <OptimizedImage
                      src={image.image_url}
                      width={100}
                      height={100}
                      mode="cover"
                      alt={`Miniatura ${index + 1}`}
                      className="w-100 h-100"
                      style={{ objectFit: 'cover' }}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <style>
        {`
          .modal-90w {
            max-width: 90vw;
          }
          @media (max-width: 991.98px) {
            .modal-90w {
              max-width: 100vw;
            }
          }
        `}
      </style>
    </>
  );
};

export default ImageGallery;
